import { getApiUrl } from "../utils/envConfig";
import { makeAuthenticatedRequest } from "./marketOverviewApi";

// Map from dimension key to URL path segment
const DIMENSION_PATH = {
  platforms: "platforms",
  franchises: "franchises",
  geo_level_1: "geo-level-1",
  geo_level_2: "geo-level-2",
  cities: "cities",
  cuisines: "cuisines",
  segment_types: "segment-types",
  price_levels: "price-levels",
  postal_codes: "postal-codes",
  crm_client_ids: "crm-client-ids",
  place_ids: "place-ids",
};

/**
 * Fetch a single filter dimension with optional pagination and search.
 *
 * @param {object} params
 * @param {string} params.dimension - One of the keys in DIMENSION_PATH
 * @param {string} params.id_client
 * @param {string} params.country - Single country code
 * @param {string} [params.refresh] - Optional YYYYMM refresh period
 * @param {number} [params.page=1]
 * @param {number} [params.limit=50]
 * @param {string} [params.search] - Optional text search
 * @param {AbortSignal} [params.signal] - Optional cancellation signal
 * @returns {Promise<{ items: any[], has_more: boolean, page: number, limit: number, dimension: string, country: string, refresh: string }>}
 */
export const fetchFilterDimension = async ({
  dimension,
  id_client,
  country,
  refresh,
  page = 1,
  limit = 50,
  search,
  signal,
}: any): Promise<any> => {
  const path = DIMENSION_PATH[dimension];
  if (!path) {
    throw new Error(`Unknown filter dimension: ${dimension}`);
  }

  const params = new URLSearchParams({ id_client, country, page: String(page), limit: String(limit) });
  if (refresh) params.set("refresh", refresh);
  if (search) params.set("search", search);

  const url = `${getApiUrl()}/filter-dimensions/${path}?${params.toString()}`;
  return makeAuthenticatedRequest(url, { signal });
};

/**
 * Fetch the two-level segment hierarchy for one scope.
 *
 * Separate from fetchFilterDimension because the response is a tree rather than a flat
 * list: each entry is a segment_type with the sub_segments seen beneath it. A scope the
 * segmenter does not cover returns every parent with an empty child list.
 *
 * @param {object} params
 * @param {string} params.id_client
 * @param {string} params.country - Single country code
 * @param {string} [params.refresh] - Optional YYYYMM refresh period
 * @param {AbortSignal} [params.signal] - Optional cancellation signal
 * @returns {Promise<{ items: { segment_type: string, sub_segments: string[] }[], dimension: string, country: string, refresh: string }>}
 */
export const fetchSegmentTree = async ({
  id_client,
  country,
  refresh,
  signal,
}: any): Promise<any> => {
  const params = new URLSearchParams({ id_client, country });
  if (refresh) params.set("refresh", refresh);

  const url = `${getApiUrl()}/filter-dimensions/segment-tree?${params.toString()}`;
  return makeAuthenticatedRequest(url, { signal });
};

/**
 * Fetch the client's sales regions, each with the territories inside it, for one scope.
 *
 * Empty for a market without sales territories. A failed request rejects, so the
 * filter can show an error rather than an empty tree.
 *
 * @returns {Promise<{ items: { sales_region: string, territories: string[] }[], dimension: string, country: string, refresh: string }>}
 */
export const fetchTerritoryTree = async ({
  id_client,
  country,
  refresh,
  signal,
}: any): Promise<any> => {
  const params = new URLSearchParams({ id_client, country });
  if (refresh) params.set("refresh", refresh);

  const url = `${getApiUrl()}/filter-dimensions/territory-tree?${params.toString()}`;
  return makeAuthenticatedRequest(url, { signal });
};

export { DIMENSION_PATH };
